<template>
  <div class="rewards-modal">
    <ion-header class="header-container">
      <ion-toolbar class="header-toolbar">
        <ion-title class="page-title">{{ $props.title }}</ion-title>
      </ion-toolbar>
    </ion-header>

    <div class="search-container">
      <ion-searchbar
        @ionInput="searchbarInput($event)"
        placeholder="Search rewards"
        class="search-rewards"
      ></ion-searchbar>
    </div>

    <ion-content class="rewards-content">
      <ion-list v-if="filteredItems.length > 0" class="rewards-list">
        <ion-item
          v-for="item in filteredItems"
          :key="item.id"
          class="reward-item"
          lines="full"
        >
          <ion-checkbox
            slot="start"
            :value="item.title"
            :checked="isChecked(item.title)"
            @ionChange="radioboxChange($event)"
            class="reward-checkbox"
            :disabled="
              workingSelectedValues.length >= 1 &&
              workingSelectedValues.indexOf(item.title) == -1
            "
          ></ion-checkbox>
          <ion-label class="reward-label">
            <h2 class="reward-title">{{ item.title }}</h2>
            <p class="reward-description">{{ item.description }}</p>
          </ion-label>
        </ion-item>
      </ion-list>

      <div v-else class="empty-state">
        <ion-icon
          src="assets/icon/gift-outline.svg"
          class="empty-icon"
        ></ion-icon>
        <p class="empty-text">No rewards available</p>
        <p class="empty-subtext">Check back later for new rewards!</p>
      </div>
    </ion-content>

    <ion-footer class="footer-container">
      <ion-toolbar class="footer-toolbar">
        <ion-buttons slot="start">
          <ion-button
            @click="cancelChanges()"
            class="footer-button back-button"
          >
            Back
          </ion-button>
        </ion-buttons>
        <ion-buttons slot="end">
          <ion-button
            @click="confirmChanges()"
            class="footer-button done-button"
          >
            Done
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-footer>
  </div>
</template>

<script>
import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonFooter,
  IonItem,
  IonLabel,
  IonList,
  IonTitle,
  IonSearchbar,
  IonToolbar,
  IonIcon,
} from "@ionic/vue";
import { defineComponent, ref } from "vue";

export default defineComponent({
  props: {
    items: Array,
    selectedItems: Array,
    title: {
      type: String,
      default: "Select Items",
    },
  },
  emits: ["selection-cancel", "selection-change"],
  components: {
    IonButton,
    IonButtons,
    IonCheckbox,
    IonContent,
    IonHeader,
    IonFooter,
    IonItem,
    IonLabel,
    IonList,
    IonTitle,
    IonSearchbar,
    IonToolbar,
    IonIcon,
  },
  setup(props, { emit }) {
    const filteredItems = ref([...props.items]);
    const workingSelectedValues = ref([...props.selectedItems]);

    const isChecked = (value) => {
      return (
        workingSelectedValues.value.find((item) => item === value) !== undefined
      );
    };

    const cancelChanges = () => {
      emit("selection-cancel");
    };

    const confirmChanges = () => {
      emit("selection-change", workingSelectedValues.value);
    };

    const searchbarInput = (ev) => {
      filterList(ev.target.value);
    };

    /**
     * Update the rendered view with
     * the provided search query. If no
     * query is provided, all data
     * will be rendered.
     */

    const filterList = (searchQuery) => {
      /**
       * If no search query is defined,
       * return all options.
       */
      if (searchQuery === undefined) {
        filteredItems.value = [...props.items];
      } else {
        /**
         * Otherwise, normalize the search
         * query and check to see which items
         * contain the search query as a substring.
         */
        const normalizedQuery = searchQuery.toLowerCase();
        filteredItems.value = props.items.filter((item) => {
          return item.description.toLowerCase().includes(normalizedQuery);
        });
      }
    };

    const radioboxChange = (ev) => {
      const { checked, value } = ev.detail;

      if (checked) {
        workingSelectedValues.value = [...workingSelectedValues.value, value];
      } else {
        workingSelectedValues.value = workingSelectedValues.value.filter(
          (item) => item !== value
        );
      }
    };
    return {
      filteredItems,
      workingSelectedValues,
      isChecked,
      cancelChanges,
      confirmChanges,
      searchbarInput,
      radioboxChange,
    };
  },
});
</script>
<style scoped>
.rewards-modal {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* Header */
.header-container {
  --background: #f87e63;
}

.header-toolbar {
  --background: #f87e63;
  --border-width: 0;
  --padding-top: 12px;
  --padding-bottom: 12px;
  --min-height: 56px;
}

.page-title {
  --color: #ffffff;
  font-family: "Gilroy", sans-serif;
  font-weight: 600;
  font-size: 18px;
  text-align: center;
}

/* Search */
.search-container {
  padding: 16px;
  background: #ffffff;
}

.search-rewards {
  --background: #f5f5f5;
  --border-radius: 10px;
  --box-shadow: none;
  --placeholder-color: #828282;
  --icon-color: #828282;
  font-family: "Gilroy", sans-serif;
  font-size: 14px;
  padding: 0;
}

/* Content */
.rewards-content {
  --background: #ffffff;
  flex: 1;
}

.rewards-list {
  padding: 0;
  margin: 0;
  background: transparent;
}

.reward-item {
  --padding-start: 16px;
  --padding-end: 16px;
  --inner-padding-end: 0;
  --min-height: 72px;
  --background: #ffffff;
}

.reward-item:last-child {
  --border-width: 0;
}

.reward-checkbox {
  --size: 22px;
  --border-radius: 4px;
  --border-color: #d1d1d1;
  --border-width: 2px;
  --checkbox-background-checked: #f87e63;
  --border-color-checked: #f87e63;
  --checkmark-color: #ffffff;
  margin-right: 12px;
}

.reward-label {
  margin: 0;
}

.reward-title {
  font-family: "Gilroy", sans-serif;
  font-weight: 600;
  font-size: 16px;
  color: #1b1b1b;
  margin: 0 0 4px 0;
  text-transform: uppercase;
}

.reward-description {
  font-family: "Gilroy", sans-serif;
  font-weight: 400;
  font-size: 13px;
  color: #828282;
  margin: 0;
  line-height: 1.4;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.empty-icon {
  width: 64px;
  height: 64px;
  color: #d1d1d1;
  margin-bottom: 16px;
}

.empty-text {
  font-family: "Gilroy", sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: #1b1b1b;
  margin: 0 0 8px 0;
}

.empty-subtext {
  font-family: "Gilroy", sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #828282;
  margin: 0;
}

/* Footer */
.footer-container {
  --background: #ffffff;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
}

.footer-toolbar {
  --background: #ffffff;
  --border-width: 0;
  --padding-start: 8px;
  --padding-end: 8px;
  --min-height: 56px;
}

.footer-button {
  font-family: "Gilroy", sans-serif;
  font-weight: 600;
  font-size: 16px;
  --padding-start: 16px;
  --padding-end: 16px;
  text-transform: none;
}

.back-button {
  --color: #828282;
}

.done-button {
  --color: #f87e63;
}
</style>
